import type { SortingState } from '@tanstack/react-table'
import { dispositionLabel } from '@/features/deal-review/deal-lead-labels'
import { getLeadDispositionOption } from '@/features/deal-review/review-options'
import {
  extractErrorMessage,
  showError,
  showInfo,
  showSuccess,
} from '@/lib/toast'
import type {
  DealCloudSyncState,
  DealDisposition,
  DealLeadDetail,
  DealLeadListItem,
  DealProcessingState,
  Filter,
  Sort,
} from '@/types/api'

export const DEAL_LEAD_VIEWS = ['inbox', 'history'] as const

export type DealLeadView = (typeof DEAL_LEAD_VIEWS)[number]

export const DEAL_LEAD_VIEW_LABELS: Record<DealLeadView, string> = {
  inbox: 'Inbox',
  history: 'History',
}

export const REVIEW_FILTERS = [
  { value: 'all', label: 'All' },
  ...(['PURSUE', 'PASS', 'NEEDS_INFORMATION'] as const).map((disposition) => ({
    value: disposition,
    label: dispositionLabel(disposition),
  })),
] as const satisfies readonly { value: string; label: string }[]

export interface DealLeadFilterState {
  search: string
  review: string
  source: string[]
  received: string
  industry: string[]
}

export const EMPTY_LEAD_FILTERS: DealLeadFilterState = {
  search: '',
  review: 'all',
  source: [],
  received: 'all',
  industry: [],
}

export const RECEIVED_WINDOWS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
] as const

// History is 'decided OR archived' and a request has one OR group, so the view is a server flag.
export function filtersForView(
  view: DealLeadView,
  state: DealLeadFilterState = EMPTY_LEAD_FILTERS
): Filter[] {
  const filters: Filter[] = []

  const search = state.search.trim()
  if (search.length > 0) {
    // OR-marked filters form one group ANDed with the rest: <view> AND (company OR project).
    for (const field of ['companyName', 'projectName'] as const) {
      filters.push({ field, op: 'contains', value: search, logicalOp: 'OR' })
    }
  }

  if (state.review !== 'all' && view === 'history') {
    filters.push({
      field: 'disposition',
      op: 'eq',
      value: state.review,
      logicalOp: 'AND',
    })
  }

  // An empty `in` would match nothing, so an empty set sends no filter.
  if (state.source.length > 0) {
    filters.push({
      field: 'brokerCompany',
      op: 'in',
      value: state.source,
      logicalOp: 'AND',
    })
  }

  if (state.industry.length > 0) {
    filters.push({
      field: 'sector',
      op: 'in',
      value: state.industry,
      logicalOp: 'AND',
    })
  }

  const days = Number(state.received)
  if (Number.isFinite(days) && days > 0) {
    const since = new Date()
    since.setDate(since.getDate() - days)
    filters.push({
      field: 'intakeMessage.receivedAt',
      op: 'gte',
      value: since.toISOString(),
      logicalOp: 'AND',
    })
  }

  return filters
}

export function hasActiveLeadFilters(state: DealLeadFilterState): boolean {
  return (
    state.search.trim().length > 0 ||
    state.review !== 'all' ||
    state.source.length > 0 ||
    state.received !== 'all' ||
    state.industry.length > 0
  )
}

const SORT_FIELDS: Record<string, string> = {
  receivedAt: 'intakeMessage.receivedAt',
  revenue: 'revenue',
  ebitda: 'ebitda',
  ebitdaMargin: 'ebitdaMarginPct',
}

const NEWEST_FIRST: Sort[] = [
  { field: 'intakeMessage.receivedAt', direction: 'desc', order: 1 },
]

export function sortsForView(sorting: SortingState): Sort[] {
  const sorts = sorting.flatMap((sort, index) => {
    const field = SORT_FIELDS[sort.id]

    if (!field) {
      if (import.meta.env.DEV) {
        console.warn(
          `[deal-review] column "${sort.id}" is sortable but has no SORT_FIELDS entry; its sort was ignored`
        )
      }
      return []
    }

    const direction: Sort['direction'] = sort.desc ? 'desc' : 'asc'
    return [{ field, direction, order: index }]
  })

  return sorts.length > 0 ? sorts : NEWEST_FIRST
}

export type DealLeadStatusTone = 'neutral' | 'positive' | 'negative' | 'warning'

export interface DealLeadStatus {
  label: string
  tone: DealLeadStatusTone
}

const DISPOSITION_TONE: Record<DealDisposition, DealLeadStatusTone> = {
  UNREVIEWED: 'neutral',
  PURSUE: 'positive',
  PASS: 'negative',
  NEEDS_INFORMATION: 'warning',
  // Retired: nothing writes this. Kept for records made while it was live.
  ARCHIVED: 'neutral',
}

const SYNC_STATUS: Partial<Record<DealCloudSyncState, DealLeadStatus>> = {
  QUEUED: { label: 'Queued for DealCloud', tone: 'neutral' },
  SENDING: { label: 'Sending to DealCloud', tone: 'warning' },
  SENT: { label: 'Sent to DealCloud', tone: 'positive' },
  FAILED: { label: 'DealCloud send failed', tone: 'negative' },
  DUPLICATE: { label: 'Set aside as duplicate', tone: 'neutral' },
}

// Only a decided lead is waiting to be sent; an undecided one has nothing to send yet.
const NOT_SENT_STATUS: DealLeadStatus = {
  label: 'Not sent to DealCloud',
  tone: 'neutral',
}

const PROCESSING_STATUS: Partial<Record<DealProcessingState, DealLeadStatus>> =
  {
    QUEUED: { label: 'In progress', tone: 'neutral' },
    PROCESSING: { label: 'In progress', tone: 'neutral' },
    FAILED: { label: 'Processing failed', tone: 'negative' },
  }

export function processingStatusFor(
  lead: DealLeadListItem
): DealLeadStatus | null {
  return PROCESSING_STATUS[lead.processingState] ?? null
}

export function statusFor(lead: DealLeadListItem): DealLeadStatus {
  const processing = processingStatusFor(lead)
  if (processing) return processing

  const label = dispositionLabel(lead.disposition)
  return {
    label: isArchived(lead) ? `${label} · Archived` : label,
    tone: DISPOSITION_TONE[lead.disposition],
  }
}

export function isArchived(lead: DealLeadListItem): boolean {
  return Boolean(lead.archivedAt)
}

export function syncStatusFor(lead: DealLeadListItem): DealLeadStatus | null {
  if (lead.processingState !== 'READY') return null
  if (lead.dealCloudSyncState === 'NOT_SENT') {
    return lead.disposition === 'UNREVIEWED' ? null : NOT_SENT_STATUS
  }
  return SYNC_STATUS[lead.dealCloudSyncState] ?? null
}

export function sentDealCloudEntryId(lead: DealLeadListItem): string | null {
  if (lead.dealCloudSyncState !== 'SENT' || !lead.dealCloudEntryId) return null
  return lead.dealCloudEntryId
}

export function isAwaitingExtraction(lead: DealLeadListItem): boolean {
  return lead.processingState !== 'READY'
}

export function isInProgress(lead: DealLeadListItem): boolean {
  return (
    lead.processingState === 'QUEUED' || lead.processingState === 'PROCESSING'
  )
}

// A re-read resets the message's stages, so only a re-screen leaves SCREEN standing.
export function isRescreening(lead: DealLeadDetail): boolean {
  return lead.message.completedStages.includes('SCREEN')
}

// Whitespace counts as no name, as the backend's send rule has it.
export function displayNameFor(lead: DealLeadListItem): string | null {
  return lead.companyName?.trim() || lead.projectName?.trim() || null
}

export function displayLabelFor(lead: DealLeadListItem): string {
  const name = displayNameFor(lead)
  if (name !== null) return name
  if (lead.processingState === 'FAILED') return 'Processing failed'

  // Qualified by attachment: one email yields a lead per attachment plus one for its body.
  if (isAwaitingExtraction(lead)) {
    if (!lead.subject) return 'Not yet extracted'
    return lead.sourceFileName
      ? `${lead.subject} — ${lead.sourceFileName}`
      : lead.subject
  }

  return 'Unnamed deal'
}

// Leading rides along: tailwind-merge drops a separate leading-* against an arbitrary size.
export const TITLE_CLASS = 'text-[25px]/[1.15]'

export const ARCHIVE_BLOCKED_REASON =
  'A lead can be archived once extraction has finished.'

export function sendIsReady(
  lead: DealLeadListItem,
  writeEnabled: boolean | undefined
): boolean {
  return writeEnabled === true && lead.capabilities.canSend
}

export function sendNote(lead: DealLeadListItem): string | null {
  const recorded = lead.sendWillRecordDisposition
  return recorded
    ? `Sending will record the recommended decision, ${getLeadDispositionOption(recorded).compactLabel}`
    : null
}

export function sendSucceeded(detail: DealLeadDetail): boolean {
  return detail.dealCloudSyncState === 'SENT'
}

export function sendFailureMessage(detail: DealLeadDetail): string {
  const failed = detail.activities.find(
    (activity) => activity.eventType === 'DEALCLOUD_SEND_FAILED'
  )
  return failed?.sendResult?.error ?? 'DealCloud did not accept this lead.'
}

export const DUPLICATE_SEND_FALLBACK =
  'Set aside as a duplicate: a deal with this name is already in DealCloud.'

export function sendWasSetAside(detail: DealLeadDetail): boolean {
  return detail.dealCloudSyncState === 'DUPLICATE'
}

// Activities arrive newest first, so find() reads the latest duplicate.
export function sendDuplicateMessage(detail: DealLeadDetail): string {
  const duplicate = detail.activities.find(
    (activity) => activity.eventType === 'DEALCLOUD_SEND_DUPLICATE'
  )
  return duplicate?.sendResult?.error || DUPLICATE_SEND_FALLBACK
}

export type SendActivityType = DealLeadDetail['activities'][number]['eventType']

const SEND_EVENT_TYPES = new Set<SendActivityType>([
  'DEALCLOUD_SEND_SUCCEEDED',
  'DEALCLOUD_SEND_FAILED',
  'DEALCLOUD_SEND_DUPLICATE',
])

function latestSendActivity(detail: DealLeadDetail) {
  return (
    detail.activities.find((activity) =>
      SEND_EVENT_TYPES.has(activity.eventType)
    ) ?? null
  )
}

export const RESOLVED_LABELS: Record<string, string> = {
  company: 'Company',
  dealSourceCompany: 'source firm',
  dealProfessional: 'contact',
}

export function sendPartialResultMessage(
  detail: DealLeadDetail
): string | null {
  const latest = latestSendActivity(detail)
  if (!latest || latest.eventType !== 'DEALCLOUD_SEND_FAILED') return null

  const written = Object.entries(latest.sendResult?.resolved ?? {}).filter(
    ([, how]) => how === 'found' || how === 'created'
  )
  if (written.length === 0) return null

  const clauses = written.map(
    ([key, how]) => `${RESOLVED_LABELS[key] ?? key} ${how}`
  )
  const sentence = clauses.join(clauses.length > 1 ? ' and ' : '')
  return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)} in DealCloud. The deal itself was not written.`
}

export function reportSendOutcome(detail: DealLeadDetail): void {
  if (sendSucceeded(detail)) {
    showSuccess('Sent to DealCloud.')
    return
  }
  if (sendWasSetAside(detail)) {
    showInfo(sendDuplicateMessage(detail))
    return
  }
  const partial = sendPartialResultMessage(detail)
  showError(
    partial
      ? `${sendFailureMessage(detail)} ${partial}`
      : sendFailureMessage(detail)
  )
}

export function reportSendError(error: unknown): void {
  showError(extractErrorMessage(error) ?? 'The send did not go through.')
}

export function resolvedFieldValue(how: string): string {
  const colon = how.indexOf(':')
  if (colon === -1) return how
  return `${how.slice(0, colon)}, ${how.slice(colon + 1)}`
}

export function dealCloudEntryUrl(
  entryId: string,
  environment: DealCloudLinkEnvironment = import.meta.env
): string | null {
  // Numeric only: an unchecked string in the template would be an open redirect.
  if (!/^\d+$/.test(entryId)) return null
  const template = environment.VITE_DEALCLOUD_DEAL_URL?.trim()
  if (!template?.includes('{entryId}')) return null
  return template.replace('{entryId}', encodeURIComponent(entryId))
}

interface DealCloudLinkEnvironment {
  VITE_DEALCLOUD_DEAL_URL?: string
}

export interface HeldLead {
  lead: DealLeadListItem
  index: number
  after: string | null
}

// Earliest first, so a lead anchored on another held lead finds it placed (GSCFE-142).
export function keepInPlace(
  leads: DealLeadListItem[],
  held: ReadonlyMap<string, HeldLead>
): DealLeadListItem[] {
  const missing = Array.from(held.values())
    .filter((entry) => !leads.some((lead) => lead.id === entry.lead.id))
    .sort((a, b) => a.index - b.index)
  if (missing.length === 0) return leads

  const rows = [...leads]
  for (const entry of missing) {
    const anchor =
      entry.after === null
        ? -1
        : rows.findIndex((lead) => lead.id === entry.after)
    const at =
      anchor !== -1 || entry.after === null
        ? anchor + 1
        : Math.min(Math.max(entry.index, 0), rows.length)
    rows.splice(at, 0, entry.lead)
  }
  return rows
}

/** Rows that were queued or sending and dropped off the new page: held briefly so they fade out. Display only. */
export function leftWhileSending(
  before: DealLeadListItem[],
  after: DealLeadListItem[]
): Map<string, HeldLead> {
  const still = new Set(after.map((lead) => lead.id))
  const gone = new Map<string, HeldLead>()
  before.forEach((lead, index) => {
    const inFlight =
      lead.dealCloudSyncState === 'QUEUED' ||
      lead.dealCloudSyncState === 'SENDING'
    if (!inFlight || still.has(lead.id)) return
    gone.set(lead.id, {
      lead,
      index,
      after: index > 0 ? before[index - 1].id : null,
    })
  })
  return gone
}

// Past three, names two and counts the rest: a clamped cell cut mid-list reads as complete.
export function undecidedSummary(
  ids: readonly string[],
  labels: ReadonlyMap<string, string>
): string | null {
  if (ids.length === 0) return null
  const names = ids.map((id) => labels.get(id) ?? id)
  if (names.length <= 3) {
    const last = names[names.length - 1]
    const rest = names.slice(0, -1)
    return `Could not decide: ${rest.length > 0 ? `${rest.join(', ')} and ${last}` : last}.`
  }
  return `Could not decide: ${names.slice(0, 2).join(', ')} and ${names.length - 2} more.`
}
